import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDemoTheme } from './DemoThemeContext';

const STEPS = [
  { id: 1, label: 'Initializing secure assessment environment' },
  { id: 2, label: 'Loading question modules and test cases' },
  { id: 3, label: 'Configuring AI evaluation engine' },
  { id: 4, label: 'Establishing encrypted session' },
  { id: 5, label: 'System ready' },
];

export default function LaunchSequence({ onComplete }) {
  const { isDark } = useDemoTheme();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let stepIndex = 0;
    const runStep = () => {
      if (stepIndex >= STEPS.length) {
        setTimeout(() => { setDone(true); setTimeout(onComplete, 600); }, 300);
        return;
      }
      setCurrentStep(stepIndex);
      const delay = stepIndex === STEPS.length - 1 ? 350 : 420 + Math.random() * 180;
      setTimeout(() => {
        setCompletedSteps((prev) => [...prev, stepIndex]);
        setProgress(Math.round(((stepIndex + 1) / STEPS.length) * 100));
        stepIndex += 1;
        runStep();
      }, delay);
    };
    const initial = setTimeout(runStep, 500);
    return () => clearTimeout(initial);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="launch"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.4 } }}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center"
          style={{ background: 'var(--d-bg)' }}
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mb-10 text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', boxShadow: '0 0 20px rgba(124,58,237,0.4)' }}
              >
                LQ
              </div>
              <span className="font-semibold tracking-widest text-sm uppercase" style={{ color: 'var(--d-text-1)', letterSpacing: '0.3em' }}>
                LambdaQuest
              </span>
            </div>
            <p className="text-xs font-mono" style={{ color: 'var(--d-text-3)' }}>Assessment Engine v2.4.1</p>
          </motion.div>

          {/* Steps */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="w-full max-w-sm space-y-3 mb-8 px-6">
            {STEPS.map((step, i) => {
              const isCompleted = completedSteps.includes(i);
              const isCurrent = currentStep === i && !isCompleted;
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: i <= currentStep ? 1 : 0.15, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="flex items-center gap-3 font-mono text-xs"
                >
                  {isCompleted ? (
                    <span className="text-emerald-400 w-4 flex-shrink-0">✓</span>
                  ) : isCurrent ? (
                    <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 0.8, repeat: Infinity }} className="w-4 flex-shrink-0" style={{ color: '#7c3aed' }}>▶</motion.span>
                  ) : (
                    <span className="w-4 flex-shrink-0" style={{ color: 'var(--d-text-3)' }}>○</span>
                  )}
                  <span style={{ color: isCompleted ? 'var(--d-text-2)' : isCurrent ? 'var(--d-text-hi)' : 'var(--d-text-3)', transition: 'color 0.3s' }}>
                    {step.label}
                  </span>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Progress bar */}
          <div className="w-full max-w-sm h-px rounded-full overflow-hidden mx-6" style={{ background: 'var(--d-border)' }}>
            <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg, #7c3aed, #6366f1)' }}
              initial={{ width: '0%' }} animate={{ width: `${progress}%` }} transition={{ duration: 0.3, ease: 'easeOut' }} />
          </div>
          <p className="mt-4 font-mono text-xs" style={{ color: 'var(--d-text-3)' }}>{progress}% complete</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
